import { computed, inject, Injectable, signal, type Signal, type WritableSignal } from "@angular/core";
import { first, finalize } from "rxjs";
import { NotificationsModel } from "@core/models/notifications.model";
import { NotificationsService } from "@shared/services/api-service/notifications.service";
import { LoadingStateHandlerService } from "@shared/services/loading-state/loading-state-handler.service";

export const NOTIFICATION_DATA_LOADING_CONTEXT: string = "NotificationData";
export const NOTIFICATION_DATA_LOADING_KEYS: { readonly notifications: string } = {
  notifications: "notifications",
};

@Injectable({
  providedIn: "root",
})
export class NotificationDataService {
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly loadingState: LoadingStateHandlerService = inject(LoadingStateHandlerService);

  private loaded: boolean = false;

  readonly notifications: WritableSignal<NotificationsModel[]> = signal<NotificationsModel[]>([]);
  readonly hasUnread: Signal<boolean> = computed(() => this.notifications().some((n) => !n.read));
  readonly isEmpty: Signal<boolean> = computed(() => this.loaded && this.notifications().length === 0);

  hasLoadedOnce(): boolean {
    return this.loaded;
  }

  loadNotifications(force: boolean = false): void {
    if (this.loaded && !force) return;

    this.loadingState.setDataLoading(
      NOTIFICATION_DATA_LOADING_CONTEXT,
      NOTIFICATION_DATA_LOADING_KEYS.notifications,
    );

    this.notificationsService
      .getNotifications({ pageNumber: 1, pageSize: 50 })
      .pipe(
        first(),
        finalize(() =>
          this.loadingState.setDataLoaded(
            NOTIFICATION_DATA_LOADING_CONTEXT,
            NOTIFICATION_DATA_LOADING_KEYS.notifications,
          ),
        ),
      )
      .subscribe(({ data }) => {
        this.notifications.set(data.notifications);
        this.loaded = true;
      });
  }

  markAsRead(id: number): void {
    this.notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    this.notificationsService.updateReadNotification(id).pipe(first()).subscribe();
  }

  markAllAsRead(): void {
    this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
    this.notificationsService.markAllReadNotifications().pipe(first()).subscribe();
  }
}
